import { FormEvent, useState } from 'react';
import { WarnTypes, arePasswordsEqual, validateEmail, validatePassword } from '../../../utils/helpers';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { LuMailCheck } from 'react-icons/lu';
import WarnList from '../../../components/Elements/WarnList';
import Input from '../../../components/Elements/Input';
import AuthCard from '../components/AuthCard';
import { resetPassword, sendRecoveryEmail } from '../api/recoverPassword';
import { useNotificationStore } from '../../../stores/notifications';

interface IWarn {
    message: string;
    id: WarnTypes;
}

/** Mensagem do back (o 422 do class-validator vem como lista). */
function mensagemDoErro(error: any, padrao: string): string {
    const msg = error?.response?.data?.message;
    if (Array.isArray(msg)) return msg.join(' ');
    return typeof msg === 'string' && msg ? msg : padrao;
}

/**
 * Três passos: pedir o e-mail, avisar que foi enviado e criar a senha nova. O link do e-mail
 * (`?token=...&email=...`) cai direto no último passo; quem não consegue abrir o link cola o
 * código que vem escrito no mesmo e-mail.
 */
export function PasswordRecovery() {
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const { addNotification } = useNotificationStore();
    const [warns, setWarns] = useState<IWarn[]>([]);
    const [loading, setLoading] = useState<boolean>(false);

    const tokenDoLink = params.get('token') ?? '';
    const [email, setEmail] = useState(params.get('email') ?? '');
    const [code, setCode] = useState(tokenDoLink);
    const [passo, setPasso] = useState<'email' | 'enviado' | 'senha'>(tokenDoLink ? 'senha' : 'email');

    const enviarEmail = async (event?: FormEvent<HTMLFormElement>): Promise<void> => {
        event?.preventDefault();

        const isEmailValid = validateEmail(email.trim());
        setWarns([isEmailValid].filter(e => e.status === false));
        if (!isEmailValid.status) return;

        setLoading(true);
        try {
            await sendRecoveryEmail({ email: email.trim() });
            setPasso('enviado');
        } catch (error: any) {
            setWarns([{ id: WarnTypes.SERVER, message: mensagemDoErro(error, 'Não foi possível enviar o e-mail. Tente novamente.') }]);
        }
        setLoading(false);
    };

    const salvarSenha = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault();

        const target = event.target as typeof event.target & {
            password: { value: string };
            confirmPassword: { value: string };
        };
        const password = target.password.value;
        const confirmPassword = target.confirmPassword.value;

        const validator = [
            validatePassword(password),
            arePasswordsEqual(password, confirmPassword),
            code.trim() ? { status: true, message: '', id: WarnTypes.SERVER } : { status: false, message: 'Cole o código que chegou no e-mail.', id: WarnTypes.SERVER },
        ].filter(e => e.status === false);

        setWarns(validator);
        if (validator.length !== 0) return;

        setLoading(true);
        try {
            await resetPassword({ recoverToken: code.trim(), password, email: email.trim() });
            addNotification({ type: 'info', title: 'Senha alterada. Entre com a senha nova.' });
            navigate('/auth/login');
        } catch (error: any) {
            setWarns([{ id: WarnTypes.SERVER, message: mensagemDoErro(error, 'Não foi possível trocar a senha. Tente novamente.') }]);
        }
        setLoading(false);
    };

    const voltarAoLogin = (
        <div className='auth-rodape auth-rodape--centro'>
            <button type='button' className='auth-link' onClick={() => navigate('/auth/login')}>Voltar para o login</button>
        </div>
    );

    if (passo === 'enviado') {
        return (
            <AuthCard titulo='Verifique seu e-mail'>
                <WarnList warns={warns}/>
                <div className='auth-enviado'>
                    <LuMailCheck />
                    <span>
                        Se houver uma conta com <strong>{email.trim()}</strong>, você vai receber em
                        instantes um link para criar uma senha nova. Não chegou? Confira a caixa de spam.
                    </span>
                </div>
                <button type='button' className='auth-botao' onClick={() => { setWarns([]); setPasso('senha'); }}>
                    Tenho o código
                </button>
                <button type='button' className='auth-botao auth-botao--secundario' disabled={loading} onClick={() => enviarEmail()}>
                    Reenviar e-mail
                </button>
                {voltarAoLogin}
            </AuthCard>
        );
    }

    if (passo === 'senha') {
        return (
            <AuthCard titulo='Criar senha nova' subtitulo={email ? <>Conta <strong>{email.trim()}</strong></> : undefined}>
                <form autoComplete='off' onSubmit={salvarSenha}>
                    <WarnList warns={warns}/>
                    {!email && <Input id='email' label='E-mail' type='email' value={email} onChange={(e: any) => setEmail(e.target.value)} />}
                    {!tokenDoLink && <Input id='recoverToken' label='Código do e-mail' value={code} onChange={(e: any) => setCode(e.target.value)} />}
                    <Input id='password' label='Senha nova' type='password' />
                    <Input id='confirmPassword' label='Confirmar senha nova' type='password' />
                    <button className='auth-botao' type='submit' disabled={loading}>Salvar senha</button>
                    {voltarAoLogin}
                </form>
            </AuthCard>
        );
    }

    return (
        <AuthCard titulo='Recuperação de senha' subtitulo='Informe o e-mail da conta. Enviamos um link para criar uma senha nova.'>
            <form autoComplete='off' onSubmit={enviarEmail}>
                <WarnList warns={warns}/>
                <Input id='email' label='E-mail' type='email' value={email} onChange={(e: any) => setEmail(e.target.value)} />
                <button className='auth-botao' type='submit' disabled={loading}>Enviar e-mail</button>
                {voltarAoLogin}
            </form>
        </AuthCard>
    );
}
